import { Css, HbLoadingSpinner, Icon, IconButton, Palette } from "@homebound/beam";
import { useState } from "react";
import { ProgressPill } from "src/components/ProgressPill";
import { TrackedJobEntry, useTrackedJobs } from "src/contexts/TrackedJobsContext";
import { TrackedJobStatus } from "src/generated/graphqlTypes";

/** Persistent floating panel that displays download/file generation progress across the app. */
export function DownloadTracker() {
  const { jobs, dismissTrackedJob } = useTrackedJobs();
  const [collapsed, setCollapsed] = useState(false);

  if (jobs.isEmpty) return null;

  return (
    <div css={Css.fixed.bottom3.right3.z999.wPx(360).bshModal.br8.bgWhite.oh.$}>
      {/* Header */}
      <div css={Css.df.aic.jcsb.px2.pyPx(10).bgGray800.white.cursorPointer.$} onClick={() => setCollapsed((c) => !c)}>
        <div css={Css.smSb.$}>Downloads ({jobs.length})</div>
        <Icon icon={collapsed ? "chevronUp" : "chevronDown"} color={Palette.White} />
      </div>

      {/* Job list */}
      {!collapsed && (
        <div css={Css.maxhPx(300).oa.$}>
          {jobs.map((job) => (
            <TrackedJobRow key={job.jobId} job={job} onDismiss={() => dismissTrackedJob(job.jobId)} />
          ))}
        </div>
      )}
    </div>
  );
}

type TrackedJobRowProps = { job: TrackedJobEntry; onDismiss: () => void };

function TrackedJobRow(props: TrackedJobRowProps) {
  const { job, onDismiss } = props;
  const isRunning = job.status === TrackedJobStatus.Pending || job.status === TrackedJobStatus.Running;
  const isCompleted = job.status === TrackedJobStatus.Completed;
  const isFailed = job.status === TrackedJobStatus.Failed;

  return (
    <div css={Css.df.fdc.gap1.px2.py2.bb.bcGray200.$}>
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.sm.gray900.truncate.$}>{job.title}</div>
        {(isCompleted || isFailed) && <IconButton icon="x" onClick={onDismiss} />}
      </div>

      {isRunning && (
        <div css={Css.df.aic.gap2.$}>
          <div css={Css.maxwPx(24).maxhPx(24).$}>
            <HbLoadingSpinner iconOnly />
          </div>
          <div css={Css.fg1.df.fdc.gap1.$}>
            {job.progressPercent !== undefined && (
              <ProgressPill progress={job.progressPercent} fullWidth text="complete" />
            )}
            {job.progressMessage && <div css={Css.xs.gray700.$}>{job.progressMessage}</div>}
            {job.progressPercent === undefined && !job.progressMessage && <div css={Css.xs.gray600.$}>Starting...</div>}
          </div>
        </div>
      )}

      {isCompleted && (
        <div css={Css.df.aic.gap1.$}>
          <Icon icon="check" color={Palette.Green600} />
          <span css={Css.xs.green700.$}>{job.downloadUrl ? "Download started" : "Completed"}</span>
        </div>
      )}

      {isFailed && <div css={Css.xs.red600.$}>{job.progressMessage ?? "Something went wrong. Please try again."}</div>}
    </div>
  );
}
